// src/components/doctorConsultationDcApi.js
//
// API helpers for /api/technician/doctor-consultation/
//
// NOTE: PatientTechnicianDashboard already calls this backend with a plain
// fetch() + absolute URL and no auth header, so the same convention is used here.
// If your ./api.js exposes an authenticated request wrapper, replace the body of
// `request()` below with that wrapper - nothing else in this file needs to change.

const API_ORIGIN = "https://cbackend.xraidigital.com";
const BASE = `${API_ORIGIN}/api/technician/doctor-consultation/`;

async function request(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const err = new Error(extractErrorMessage(data) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// DRF errors look like {field: ["msg"]}, {detail: "msg"} or ["msg"]
export function extractErrorMessage(data) {
  if (!data) return "";
  if (typeof data === "string") return data.length > 300 ? "" : data;
  if (Array.isArray(data)) return data.join(" ");
  if (data.detail) return String(data.detail);
  if (data.error) return String(data.error);
  if (data.message) return String(data.message);
  return Object.entries(data)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" | ");
}

// The list endpoint may return a bare array, a DRF paginated object
// ({count,next,previous,results}) or a wrapped object. Normalise all of them.
function extractList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.consultations)) return data.consultations;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

/** GET list - follows DRF `next` links (max 20 pages) so no record is missed. */
export async function listDoctorConsultations(params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();

  let url = qs ? `${BASE}?${qs}` : BASE;
  const all = [];

  for (let page = 0; page < 20 && url; page += 1) {
    const data = await request(url);
    all.push(...extractList(data));
    url = data && !Array.isArray(data) && typeof data.next === "string" ? data.next : null;
  }
  return all;
}

/** GET one */
export function getDoctorConsultation(id) {
  return request(`${BASE}${id}/`);
}

/** POST create */
export function createDoctorConsultation(payload) {
  return request(BASE, { method: "POST", body: JSON.stringify(payload) });
}

/** PATCH update (send only changed fields) */
export function updateDoctorConsultation(id, payload) {
  return request(`${BASE}${id}/`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** Make a (possibly relative) report URL absolute. Returns "" if there is none. */
export function resolveReportUrl(value) {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_ORIGIN}${value.startsWith("/") ? "" : "/"}${value}`;
}